/**
 * 🔧 요금제별 제한사항 (개발용)
 *
 * 이 파일은 **실제 개발**에서 사용됩니다.
 * - 읽기 쉬운 중첩 객체 구조
 * - TypeScript 자동완성 완벽 지원
 * - `usePlanLimits()` 훅과 함께 사용
 *
 * @see planCaps.ts - 정책 명세 (dot-key 형식)
 * @see hooks/usePlanLimits.ts - 이 파일을 사용하는 Hook
 *
 * @note API 배포 전: 이 파일 기준으로 동작
 * @note API 배포 후: usePlanLimits()만 수정하면 전환 완료
 *
 * @example
 * ```tsx
 * const { limits, canUse, hasReached } = usePlanLimits();
 *
 * // 직관적인 접근
 * if (limits.maxProjects === 3) { ... }
 * if (canUse('gps')) { ... }
 * ```
 */

export interface PlanLimits {
  // ============================================
  // 콘텐츠 제작
  // ============================================

  /** 프로젝트 생성 최대 개수 (null = 무제한) */
  maxProjects: number | null;

  /** 콘텐츠 템플릿 최대 개수 (null = 무제한) */
  maxTemplates: number | null;

  /** 기본 상호작용 도구 사용 가능 */
  basicInteractionTool: boolean;

  /** 아이템 최대 개수 (null = 무제한) */
  maxItems: number | null;

  /** 콘텐츠 자동 생성 가능 */
  contentAutoGenerate: boolean;

  /** 이미지/BGM 생성 가능 */
  contentImageAndBgmGenerate: boolean;

  /** 월간 AI 크레딧 */
  aiCreditsPerMonth: number;

  // ============================================
  // 기능
  // ============================================

  /** 기능 사용 가능 여부 */
  features: {
    gps: boolean;
    nfc: boolean;
    photoFrame: boolean;
    qr: boolean;
    ar: boolean;
    colorFilter: boolean;
    overlap: boolean;
    removeAds: boolean;
    webPlay: boolean;
    donation: boolean;
  };

  // ============================================
  // 데이터 관리
  // ============================================

  /** 데이터 조회 가능 일수 */
  dataRetentionDays: number;

  /** 데이터 다운로드 가능 */
  dataDownload: boolean;

  /** 데이터 제공 */
  data: {
    /** 콘텐츠 플레이 수 데이터 */
    playCount: boolean;
    /** 미션별 완료율 데이터 */
    missionCompletionRate: boolean;
    /** 콘텐츠 완료율 데이터 */
    contentCompletionRate: boolean;
  };

  // ============================================
  // 채널 권한
  // ============================================

  /** 팀원 초대 최대 수 */
  teamInvites: {
    /** 테스터 초대 최대 수 (null = 무제한) */
    maxTesters: number | null;
    /** 소유자 초대 최대 수 */
    maxOwners: number;
    /** 편집자 초대 최대 수 */
    maxEditors: number;
  };

  /** 동시 접속 가능 */
  simultaneousAccess: boolean;
}

// TODO: 추후제거
/**
 * 요금제별 제한사항 (2025.11 정책 기준)
 *
 * @see planCaps.ts - dot-key 명세
 */
export const PLAN_LIMITS: Record<PlanType, PlanLimits> = {
  Free: {
    // 콘텐츠 제작
    maxProjects: 3,
    maxTemplates: 2,
    basicInteractionTool: true,
    maxItems: 5,
    contentAutoGenerate: true,
    contentImageAndBgmGenerate: true,
    aiCreditsPerMonth: 100,

    // 기능
    features: {
      gps: false,
      nfc: false,
      photoFrame: false,
      qr: false,
      ar: false,
      colorFilter: false,
      overlap: false,
      removeAds: false,
      webPlay: false,
      donation: false
    },

    // 데이터 관리
    dataRetentionDays: 7,
    dataDownload: false,
    data: {
      playCount: true,
      missionCompletionRate: false,
      contentCompletionRate: false
    },

    // 채널 권한
    teamInvites: {
      maxTesters: null, // 무제한
      maxOwners: 0,
      maxEditors: 0
    },
    simultaneousAccess: false
  },

  Pro: {
    // 콘텐츠 제작
    maxProjects: 10,
    maxTemplates: 5,
    basicInteractionTool: true,
    maxItems: null, // 무제한
    contentAutoGenerate: true,
    contentImageAndBgmGenerate: true,
    aiCreditsPerMonth: 500,

    // 기능
    features: {
      gps: true,
      nfc: true,
      photoFrame: true,
      qr: true,
      ar: true,
      colorFilter: true,
      overlap: true,
      removeAds: false,
      webPlay: false,
      donation: true
    },

    // 데이터 관리
    dataRetentionDays: 30,
    dataDownload: true,
    data: {
      playCount: true,
      missionCompletionRate: true,
      contentCompletionRate: false
    },

    // 채널 권한
    teamInvites: {
      maxTesters: null, // 무제한
      maxOwners: 0,
      maxEditors: 0
    },
    simultaneousAccess: false
  },

  Enterprise: {
    // 콘텐츠 제작
    maxProjects: null, // 무제한
    maxTemplates: null,
    basicInteractionTool: true,
    maxItems: null,
    contentAutoGenerate: true,
    contentImageAndBgmGenerate: true,
    aiCreditsPerMonth: 10000,

    // 기능
    features: {
      gps: true,
      nfc: true,
      photoFrame: true,
      qr: true,
      ar: true,
      colorFilter: true,
      overlap: true,
      removeAds: true,
      webPlay: true,
      donation: true
    },

    // 데이터 관리
    dataRetentionDays: 365,
    dataDownload: true,
    data: {
      playCount: true,
      missionCompletionRate: true,
      contentCompletionRate: true
    },

    // 채널 권한
    teamInvites: {
      maxTesters: null, // 무제한
      maxOwners: 3,
      maxEditors: 20
    },
    simultaneousAccess: true
  },

  Class: {
    // 콘텐츠 제작
    maxProjects: null, // 무제한
    maxTemplates: null,
    basicInteractionTool: true,
    maxItems: null,
    contentAutoGenerate: true,
    contentImageAndBgmGenerate: true,
    aiCreditsPerMonth: 10000,

    // 기능
    features: {
      gps: true,
      nfc: true,
      photoFrame: true,
      qr: true,
      ar: true,
      colorFilter: true,
      overlap: true,
      removeAds: true,
      webPlay: true,
      donation: false // School은 후원받기 불가
    },

    // 데이터 관리
    dataRetentionDays: 365,
    dataDownload: true,
    data: {
      playCount: true,
      missionCompletionRate: true,
      contentCompletionRate: true
    },

    // 채널 권한
    teamInvites: {
      maxTesters: null, // 무제한
      maxOwners: 3,
      maxEditors: 20
    },
    simultaneousAccess: true
  },
  Teacher: {
    // 콘텐츠 제작
    maxProjects: 10,
    maxTemplates: 5,
    basicInteractionTool: true,
    maxItems: null, // 무제한
    contentAutoGenerate: true,
    contentImageAndBgmGenerate: true,
    aiCreditsPerMonth: 500,

    // 기능
    features: {
      gps: true,
      nfc: true,
      photoFrame: true,
      qr: true,
      ar: true,
      colorFilter: true,
      overlap: true,
      removeAds: false,
      webPlay: false,
      donation: true
    },

    // 데이터 관리
    dataRetentionDays: 30,
    dataDownload: true,
    data: {
      playCount: true,
      missionCompletionRate: true,
      contentCompletionRate: false
    },

    // 채널 권한
    teamInvites: {
      maxTesters: null, // 무제한
      maxOwners: 0,
      maxEditors: 0
    },
    simultaneousAccess: false
  },
  Realworld: {
    // 콘텐츠 제작
    maxProjects: null, // 무제한
    maxTemplates: null,
    basicInteractionTool: true,
    maxItems: null,
    contentAutoGenerate: true,
    contentImageAndBgmGenerate: true,
    aiCreditsPerMonth: 10000,

    // 기능
    features: {
      gps: true,
      nfc: true,
      photoFrame: true,
      qr: true,
      ar: true,
      colorFilter: true,
      overlap: true,
      removeAds: true,
      webPlay: true,
      donation: false // School은 후원받기 불가
    },

    // 데이터 관리
    dataRetentionDays: 365,
    dataDownload: true,
    data: {
      playCount: true,
      missionCompletionRate: true,
      contentCompletionRate: true
    },

    // 채널 권한
    teamInvites: {
      maxTesters: null, // 무제한
      maxOwners: 3,
      maxEditors: 20
    },
    simultaneousAccess: true
  }
};

/**
 * 헬퍼 함수들
 */

/** 특정 플랜의 제한사항 가져오기 */
export function getLimitsForPlan(plan: PlanType | null | undefined): PlanLimits {
  return PLAN_LIMITS[plan ?? 'Free'];
}

/** 특정 기능 사용 가능 여부 체크 */
export function canUseFeature(
  plan: PlanType | null | undefined,
  feature: keyof PlanLimits['features']
): boolean {
  return getLimitsForPlan(plan).features[feature];
}

/** 제한 도달 여부 체크 */
export function hasReachedLimit(
  plan: PlanType | null | undefined,
  limitType: 'projects' | 'templates' | 'items',
  currentCount: number
): boolean {
  const limits = getLimitsForPlan(plan);
  let maxKey: 'maxProjects' | 'maxTemplates' | 'maxItems';

  if (limitType === 'projects') {
    maxKey = 'maxProjects';
  } else if (limitType === 'templates') {
    maxKey = 'maxTemplates';
  } else {
    maxKey = 'maxItems';
  }

  const max = limits[maxKey];

  // null = 무제한
  if (max === null) {
    return false;
  }

  return currentCount >= max;
}

/** 남은 개수 계산 */
export function getRemainingCount(
  plan: PlanType | null | undefined,
  limitType: 'projects' | 'templates' | 'items',
  currentCount: number
): number | null {
  const limits = getLimitsForPlan(plan);
  let maxKey: 'maxProjects' | 'maxTemplates' | 'maxItems';

  if (limitType === 'projects') {
    maxKey = 'maxProjects';
  } else if (limitType === 'templates') {
    maxKey = 'maxTemplates';
  } else {
    maxKey = 'maxItems';
  }

  const max = limits[maxKey];

  // null = 무제한
  if (max === null) {
    return null;
  }

  return Math.max(0, max - currentCount);
}

export type PeriodOptionValue =
  | 'custom'
  | 'today'
  | 'yesterday'
  | 'week'
  | 'month'
  | '3months'
  | '6months'
  | 'year';

export interface PeriodOption {
  label: string;
  value: PeriodOptionValue;
  requiredPlan: PlanType | null; // null이면 모든 플랜 가능
  days: number; // 해당 기간의 일수
}

export const PERIODS: PeriodOption[] = [
  { label: '기간설정', value: 'custom', requiredPlan: null, days: 0 },
  { label: '오늘', value: 'today', requiredPlan: null, days: 0 },
  { label: '어제', value: 'yesterday', requiredPlan: null, days: 1 },
  { label: '일주일', value: 'week', requiredPlan: null, days: 7 },
  { label: '한 달', value: 'month', requiredPlan: 'Pro', days: 30 }, // pro 이상 필요
  {
    label: '3달',
    value: '3months',
    requiredPlan: ['Enterprise', 'Class', 'Realworld'] as unknown as PlanType | null,
    days: 90
  },
  {
    label: '6달',
    value: '6months',
    requiredPlan: ['Enterprise', 'Class', 'Realworld'] as unknown as PlanType | null,
    days: 180
  },
  {
    label: '1년',
    value: 'year',
    requiredPlan: ['Enterprise', 'Class', 'Realworld'] as unknown as PlanType | null,
    days: 365
  }
];

// 플랜 레벨 정의 (높을수록 상위 플랜)
const PLAN_LEVELS: Record<PlanType, number> = {
  Free: 0,
  Pro: 1,
  Enterprise: 2,
  Class: 2, // School과 Enterprise는 동일 레벨
  Teacher: 1,
  Realworld: 3
};

// 특정 기간을 사용할 수 있는지 체크
export function canUsePeriod(period: PeriodOption, currentPlan: PlanType): boolean {
  if (period.requiredPlan === null) {
    return true;
  }

  if (Array.isArray(period.requiredPlan)) {
    return period.requiredPlan.includes(currentPlan);
  }

  return PLAN_LEVELS[currentPlan] >= PLAN_LEVELS[period.requiredPlan];
}
